// src/components/managed-sources/VerityPixelWebsiteStep.tsx
/**
 * Connect step of the Verity Pixel wizard (plan 2026-10-02 verity pixel, D11):
 * choose the website the pixel goes on, or add one by its address.
 *
 * There is no authorization to pick: the backend authorizes the source with
 * the account's own warehouse access. What this step hands the wizard is the
 * `connector_config` the setup call takes, in one of two shapes:
 *   - `{ website_id }` for a website the account already has, or
 *   - `{ website_url, confirm_owned: true }` to add one.
 * The backend owns validation (address, ownership, one pixel per website) and
 * answers with a customer-readable detail the wizard shows at launch.
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Checkbox,
  Group,
  Loader,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";

import { listWebsites, type WebsiteListItem } from "@/api/websites";

export type VerityPixelConnectorConfig =
  | { website_id: string }
  | { website_url: string; confirm_owned: true };

type Props = {
  /** The setup `connector_config`, or null while the step is incomplete; plus
   *  a label for the review step and the default source name. */
  onConfigChange: (config: VerityPixelConnectorConfig | null, label: string | null) => void;
  onValidated: (valid: boolean) => void;
};

type Mode = "existing" | "new";

function websiteLabel(w: WebsiteListItem): string {
  return w.name && w.name !== w.host ? `${w.name} (${w.host})` : w.host;
}

/** The host of a typed address, for the review label; the backend normalises for real. */
function hostOf(raw: string): string {
  const value = raw.trim();
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`).host || value;
  } catch {
    return value;
  }
}

export default function VerityPixelWebsiteStep({ onConfigChange, onValidated }: Props) {
  const [websites, setWebsites] = useState<WebsiteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("new");
  const [websiteId, setWebsiteId] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [confirmOwned, setConfirmOwned] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listWebsites()
      .then((list) => {
        if (cancelled) return;
        setWebsites(list);
        setMode(list.length > 0 ? "existing" : "new");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error && e.message ? e.message : "Could not load your websites.");
        setMode("new");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mode === "existing") {
      const w = websites.find((x) => x.public_id === websiteId);
      if (w) {
        onConfigChange({ website_id: w.public_id }, w.host);
        onValidated(true);
        return;
      }
    } else if (url.trim() && confirmOwned) {
      onConfigChange({ website_url: url.trim(), confirm_owned: true }, hostOf(url));
      onValidated(true);
      return;
    }
    onConfigChange(null, null);
    onValidated(false);
  }, [mode, websiteId, url, confirmOwned, websites, onConfigChange, onValidated]);

  if (loading) {
    return (
      <Group gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading your websites…</Text>
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap={2}>
        <Text fw={600}>Choose your website</Text>
        <Text size="sm" c="dimmed">
          Pick the website you will add the pixel to. You get the snippet to install
          after you launch the source.
        </Text>
      </Stack>

      {loadError && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Could not load your websites">
          {loadError} You can still add a website by its address below.
        </Alert>
      )}

      {websites.length > 0 && (
        <Radio.Group value={mode} onChange={(v) => setMode(v as Mode)}>
          <Stack gap={6}>
            <Radio value="existing" label="Use a website you already added" />
            <Radio value="new" label="Add a new website" />
          </Stack>
        </Radio.Group>
      )}

      {mode === "existing" && websites.length > 0 && (
        <Select
          label="Website"
          placeholder="Choose a website"
          data={websites.map((w) => ({ value: w.public_id, label: websiteLabel(w) }))}
          value={websiteId}
          onChange={setWebsiteId}
          searchable
          comboboxProps={{ withinPortal: false }}
        />
      )}

      {mode === "new" && (
        <Stack gap="sm">
          <TextInput
            label="Website address"
            placeholder="https://www.example.com"
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
          />
          <Checkbox
            label="I own this website or I am allowed to add the pixel to it."
            checked={confirmOwned}
            onChange={(e) => setConfirmOwned(e.currentTarget.checked)}
          />
        </Stack>
      )}
    </Stack>
  );
}
